/**
 * The confirmation before an environment job: what the draft adds, changes,
 * removes, and drops, and the one rule worth restating: the administrator's
 * versions replace the manifest's at resolve time, and the sealed load decides.
 */
import type { OverrideEntry } from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { Dialog, DialogActions } from '../../ui/Dialog.js';
import type { DraftSummary } from './environment-draft.js';
import { OriginBadge } from './OriginBadge.js';

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  pluginName: string;
  summary: DraftSummary;
  pending: boolean;
  onConfirm: () => void;
}

/** `name` plus its specifier, as the resolver will read it. */
function requirement(entry: OverrideEntry): string {
  return `${entry.name}${entry.specifier ?? ''}`;
}

export function ApplyEnvironmentDialog({
  open,
  onOpenChange,
  pluginName,
  summary,
  pending,
  onConfirm,
}: Props) {
  const count =
    summary.added.length + summary.changed.length + summary.removed.length + summary.dropped.length;
  return (
    <Dialog
      open={open}
      onClose={() => onOpenChange(false)}
      title={
        <>
          Apply {count} change{count === 1 ? '' : 's'} to {pluginName}?
        </>
      }
      subtitle={
        <>
          A new environment is built and verified before it replaces the current one. Your versions
          replace the manifest&apos;s at resolve time; the plugin must still load in the sealed
          worker or nothing changes.
        </>
      }
    >
      <ul
        style={{
          marginTop: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 7,
          fontSize: 'var(--text-meta-lg)',
        }}
      >
        {summary.added.map((e) => (
          <li key={e.name} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <OriginBadge origin="added" />
            <code className="mono">{requirement(e)}</code>
          </li>
        ))}
        {summary.changed.map((e) => (
          <li key={e.name} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <OriginBadge origin="changed" />
            <code className="mono">{requirement(e)}</code>
          </li>
        ))}
        {summary.removed.map((e) => (
          <li key={e.name} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <OriginBadge origin="removed" />
            <code className="mono">{e.name}</code>
            <span style={{ color: 'var(--text-muted)' }}>the sealed load decides</span>
          </li>
        ))}
        {summary.dropped.map((e) => (
          <li key={e.name} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <OriginBadge origin="declared" label="reverted" />
            <code className="mono">{e.name}</code>
            <span style={{ color: 'var(--text-muted)' }}>the manifest decides again</span>
          </li>
        ))}
      </ul>
      {summary.kept.length > 0 && (
        <p style={{ marginTop: 11, fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
          Kept from before: {summary.kept.map(requirement).join(', ')}. The whole set is applied as
          one job.
        </p>
      )}
      <DialogActions>
        <Button
          type="button"
          variant="ghost"
          onClick={() => onOpenChange(false)}
          disabled={pending}
        >
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={onConfirm} disabled={pending}>
          {pending ? 'Applying…' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
